<template>
  <a-form layout="vertical">
    <a-form-item :label="t('shell.account.currentPhone')">
      <a-input :value="currentPhone || t('shell.account.phoneUnset')" disabled />
    </a-form-item>
    <a-form-item :label="t('shell.account.newPhone')">
      <a-input v-model:value="form.phone" :placeholder="t('shell.account.newPhonePlaceholder')" />
    </a-form-item>
    <a-form-item :label="t('shell.account.phoneCode')" required>
      <div class="flex items-center gap-2">
        <a-input
          v-model:value="form.phoneCode"
          :placeholder="t('shell.account.phoneCodePlaceholder')"
        />
        <a-button
          class="shrink-0"
          :loading="sending"
          :disabled="countdown > 0 || !form.phone.trim()"
          @click="handleSendCode"
        >
          {{
            countdown > 0
              ? t('shell.account.codeResend', { seconds: countdown })
              : t('shell.account.sendCode')
          }}
        </a-button>
      </div>
    </a-form-item>
    <a-form-item>
      <a-button
        type="primary"
        :loading="saving"
        :disabled="!form.phone.trim() || !form.phoneCode.trim()"
        @click="handleSave"
      >
        {{ t('shell.account.savePhone') }}
      </a-button>
    </a-form-item>
  </a-form>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue'
import { onUnmounted, reactive, ref } from 'vue'
import { sendPhoneCode, updatePhone } from '../../api/user/setting'
import { useI18n } from '../../composables/useI18n'

defineProps<{ currentPhone: string }>()
const emit = defineEmits<{ updated: [value: string] }>()
const { t } = useI18n()

const phonePattern = /^1[3-9]\d{9}$/
const form = reactive({ phone: '', phoneCode: '' })
const saving = ref(false)
const sending = ref(false)
const countdown = ref(0)
let timer: ReturnType<typeof setInterval> | null = null

function stopCountdown() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
  countdown.value = 0
}

function startCountdown() {
  stopCountdown()
  countdown.value = 60
  timer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) stopCountdown()
  }, 1000)
}

onUnmounted(stopCountdown)

function checkPhone(): boolean {
  const phone = form.phone.trim()
  if (!phone) {
    message.warning(t('shell.account.phoneRequired'))
    return false
  }
  if (!phonePattern.test(phone)) {
    message.warning(t('shell.account.phoneInvalid'))
    return false
  }
  return true
}

async function handleSendCode() {
  if (!checkPhone()) return
  sending.value = true
  try {
    await sendPhoneCode(form.phone.trim())
    message.success(t('shell.account.codeSent'))
    startCountdown()
  } catch (e: any) {
    message.error(e?.message || t('shell.account.codeSendFailed'))
  } finally {
    sending.value = false
  }
}

async function handleSave() {
  if (!checkPhone()) return
  const phoneCode = form.phoneCode.trim()
  if (!phoneCode) {
    message.warning(t('shell.account.phoneCodeRequired'))
    return
  }
  saving.value = true
  try {
    await updatePhone(form.phone.trim(), phoneCode)
    message.success(t('shell.account.phoneSuccess'))
    emit('updated', form.phone.trim())
    form.phone = ''
    form.phoneCode = ''
    stopCountdown()
  } catch (e: any) {
    message.error(e?.message || t('shell.account.updateFailed'))
  } finally {
    saving.value = false
  }
}
</script>
